import type * as React from 'react';
import { Text, View } from 'react-native';

import type * as api from '@gitbook/api-client';
import {
    BaseCard,
    flex,
    Interactive,
    sizes,
    spacing,
    themedFonts,
    useCurrentTheme,
} from '@gitbook/react-uikit';

import { ContentKitButton } from './ContentKitButton';
import { ContentKitIcon } from './ContentKitIcon';
import { ContentKitImage } from './ContentKitImage';
import { ensureStackElements } from './ContentKitStack';
import { ContentKitTextStyleContext } from './ContentKitText';
import type { ContentKitElementProps } from './types';

export function ContentKitCard(
    props: ContentKitElementProps<api.ContentKitCard>
): React.ReactElement {
    const { element, renderElements, dispatchAction } = props;
    const [, font] = useCurrentTheme(themedFonts);

    const header = element.title ? (
        <View style={[flex.row, flex.alignItemsCenter]}>
            {element.icon ? (
                <View style={[spacing.Padding.all400, spacing.Padding.right0]}>
                    {typeof element.icon === 'string' ? (
                        <ContentKitIcon icon={element.icon} size={18} className="text-muted" />
                    ) : (
                        <ContentKitImage
                            {...props}
                            element={element.icon}
                            style={{ width: sizes.ICON_400 }}
                        />
                    )}
                </View>
            ) : null}
            <View style={[flex.flex1, flex.column, spacing.Padding.all400]}>
                <View style={[flex.flex1, element.hint ? spacing.Margin.bottom100 : undefined]}>
                    <Interactive.Text.Child
                        descendantOf={BaseCard.interactiveHandle}
                        style={[font.text, font.Body, font.Medium]}
                        activeStyle={font.primary}
                    >
                        {element.title}
                    </Interactive.Text.Child>
                </View>
                {element.hint ? (
                    <View style={[flex.flex1]}>
                        <Interactive.View.Child descendantOf={BaseCard.interactiveHandle}>
                            {typeof element.hint === 'string' ? (
                                <Text style={[font.text, font.Caption, font.textMuted]}>
                                    {element.hint}
                                </Text>
                            ) : (
                                <ContentKitTextStyleContext.Provider
                                    value={[font.Caption, font.textMuted]}
                                >
                                    {renderElements(
                                        'inline',
                                        ensureStackElements('hstack', element.hint, 'start')
                                    )}
                                </ContentKitTextStyleContext.Provider>
                            )}
                        </Interactive.View.Child>
                    </View>
                ) : null}
            </View>
            {element.buttons && element.buttons.length > 0 ? (
                <View style={[spacing.Padding.vertical200, spacing.Padding.right400]}>
                    {element.buttons.map((button, index) => (
                        <View key={index} style={[spacing.Margin.left200]}>
                            <ContentKitButton
                                {...props}
                                element={button}
                                buttonDefaultKind={'blank'}
                            />
                        </View>
                    ))}
                </View>
            ) : null}
        </View>
    ) : null;

    return (
        <BaseCard
            {...(element.onPress
                ? {
                      component: Interactive.TouchableOpacity,
                      onPress: () => {
                          if (element.onPress) {
                              dispatchAction(element.onPress);
                          }
                      },
                  }
                : {
                      component: View,
                  })}
        >
            <View style={[flex.column, flex.flex1]}>
                {header}
                {element.children && element.children.length > 0 ? (
                    <View
                        style={[
                            header
                                ? [spacing.Padding.horizontal400, spacing.Padding.bottom400]
                                : [spacing.Padding.all400],
                        ]}
                    >
                        {renderElements('block', ensureStackElements('vstack', element.children))}
                    </View>
                ) : null}
            </View>
        </BaseCard>
    );
}
